'use client';

import { c as _c } from "react/compiler-runtime";
import { app, object, tray } from '@todesktop/client-core';
import { useEffect, useState } from 'react';
import { getReviewQueueBadgeCount } from '@/components/desktop/review-queue';
import { useDesktopReviewQueue } from '@/components/desktop/use-desktop-review-queue';
import { clientLogger } from '@/lib/client-logger';
import { DESKTOP_TRAY_ID } from '@/lib/desktop-tray-window';
type TrayReference = NonNullable<Awaited<ReturnType<typeof object.retrieve>>>;
function formatTrayTitle(count: number): string {
  return count > 0 ? String(count) : '';
}
function formatTrayTooltip(count: number): string {
  if (count === 0) {
    return 'cubic';
  }
  return `${count} ${count === 1 ? 'pull request needs' : 'pull requests need'} you`;
}

/**
 * Shows how many pull requests need the user, on the app badge and next to the tray icon: the
 * rows of the tray's open sections, the inbox's Needs you and Ready to merge. Clicking the tray
 * icon opens the review queue popover, a separate window that loads `/desktop/tray`, so no native
 * tray menu is built here.
 */
export function DesktopTrayBadgeProvider() {
  const $ = _c(11);
  const t0 = useDesktopReviewQueue();
  let t1;
  if ($[0] !== t0) {
    t1 = getReviewQueueBadgeCount(t0);
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const badgeCount = t1;
  const [trayReference, setTrayReference] = useState(null);
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      let cancelled = false;
      object.retrieve({
        id: DESKTOP_TRAY_ID
      }).then(reference => {
        if (!cancelled && reference) {
          setTrayReference(reference);
        }
      }).catch(_temp);
      return () => {
        cancelled = true;
      };
    };
    t3 = [];
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  let t5;
  if ($[4] !== badgeCount) {
    t4 = () => {
      if (badgeCount === null) {
        return;
      }
      app.setBadgeCount(badgeCount).catch(_temp2);
    };
    t5 = [badgeCount];
    $[4] = badgeCount;
    $[5] = t4;
    $[6] = t5;
  } else {
    t4 = $[5];
    t5 = $[6];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[7] !== badgeCount || $[8] !== trayReference) {
    t6 = () => {
      if (!trayReference || badgeCount === null) {
        return;
      }
      Promise.all([tray.setTitle({
        ref: trayReference,
        title: formatTrayTitle(badgeCount)
      }), tray.setToolTip({
        ref: trayReference,
        tooltip: formatTrayTooltip(badgeCount)
      })]).catch(_temp3);
    };
    t7 = [badgeCount, trayReference];
    $[7] = badgeCount;
    $[8] = trayReference;
    $[9] = t6;
    $[10] = t7;
  } else {
    t6 = $[9];
    t7 = $[10];
  }
  useEffect(t6, t7);
  return null;
}
function _temp3(error_1) {
  clientLogger.error("Failed to update the desktop tray count", error_1);
}
function _temp2(error_0) {
  clientLogger.error("Failed to update the desktop app badge count", error_0);
}
function _temp(error) {
  clientLogger.error("Failed to retrieve the ToDesktop tray reference", error);
}